import React, { useState, useEffect } from 'react';
import Layout from '../components/Layout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Plus, Trash2, Ban } from 'lucide-react';
import { toast } from 'sonner';
import api from '../utils/api';
import { formatDate } from '../utils/helpers';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Label } from '../components/ui/label';

const BlackoutDaysPage = ({ user, onLogout }) => {
  const [blackouts, setBlackouts] = useState([]);
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [formData, setFormData] = useState({
    service_ids: [],
    dates: [],
    reason: '',
    start_date: '',
    end_date: ''
  });

  useEffect(() => {
    fetchBlackouts();
    fetchServices();
  }, []);

  const fetchBlackouts = async () => {
    try {
      const response = await api.get('/blackout-days');
      setBlackouts(response.data);
    } catch (error) {
      toast.error('Failed to fetch blackout days');
    } finally {
      setLoading(false);
    }
  };

  const fetchServices = async () => {
    try {
      const response = await api.get('/services');
      setServices(response.data.filter(s => s.status === 'active'));
    } catch (error) {
      toast.error('Failed to fetch services');
    }
  };

  const generateDateRange = (start, end) => {
    const dates = [];
    const startDate = new Date(start);
    const endDate = new Date(end);
    
    for (let d = new Date(startDate); d <= endDate; d.setDate(d.getDate() + 1)) {
      dates.push(d.toISOString().split('T')[0]);
    }
    
    return dates;
  };

  const handleCreate = async () => {
    if (formData.service_ids.length === 0) {
      toast.error('Please select at least one service');
      return;
    }

    if (!formData.start_date || !formData.end_date) {
      toast.error('Please select start and end dates');
      return;
    }

    if (new Date(formData.end_date) < new Date(formData.start_date)) {
      toast.error('End date must be after start date');
      return;
    }

    try {
      const dates = generateDateRange(formData.start_date, formData.end_date);
      const selectedServices = services.filter(s => formData.service_ids.includes(s.id));
      
      await api.post('/blackout-days', {
        service_ids: formData.service_ids,
        service_names: selectedServices.map(s => s.name),
        dates: dates,
        reason: formData.reason,
        created_by_user_id: user.id
      });
      
      toast.success(`Blackout created for ${dates.length} day(s)`);
      setShowCreateModal(false);
      setFormData({ service_ids: [], dates: [], reason: '', start_date: '', end_date: '' });
      fetchBlackouts();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create blackout');
    }
  };

  const handleDelete = async (blackout) => {
    if (!window.confirm(`Are you sure you want to delete this blackout? This will affect ${blackout.dates.length} day(s).`)) {
      return;
    }

    try {
      await api.delete(`/blackout-days/${blackout.id}`);
      toast.success('Blackout deleted successfully');
      fetchBlackouts();
    } catch (error) {
      toast.error('Failed to delete blackout');
    }
  };

  const toggleService = (serviceId) => {
    setFormData(prev => ({
      ...prev,
      service_ids: prev.service_ids.includes(serviceId)
        ? prev.service_ids.filter(id => id !== serviceId)
        : [...prev.service_ids, serviceId]
    }));
  };

  return (
    <Layout user={user} onLogout={onLogout}>
      <div className="p-6 max-w-6xl mx-auto">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Blackout Days</h1>
            <p className="text-sm text-gray-500 mt-1">Block services on specific dates</p>
          </div>
          {user.role === 'Admin' && (
            <Button
              onClick={() => setShowCreateModal(true)}
              className="bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700"
            >
              <Plus className="w-4 h-4 mr-2" />
              Add Blackout
            </Button>
          )}
        </div>

        {loading ? (
          <div className="text-center py-12 text-gray-500">Loading...</div>
        ) : blackouts.length === 0 ? (
          <div className="text-center py-12 bg-white rounded-xl border-2 border-dashed border-gray-200">
            <Ban className="w-12 h-12 text-gray-400 mx-auto mb-3" />
            <p className="text-gray-500">No blackout days configured</p>
          </div>
        ) : (
          <div className="grid gap-4">
            {blackouts.map(blackout => (
              <div
                key={blackout.id}
                className="bg-white border-2 border-red-200 rounded-xl p-5"
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <Ban className="w-5 h-5 text-red-600" />
                      <h3 className="text-lg font-bold text-gray-900">
                        {blackout.dates.length} day(s) blocked
                      </h3>
                    </div>
                    
                    <div className="space-y-2">
                      <div>
                        <span className="text-sm font-medium text-gray-700">Services: </span>
                        <div className="flex gap-2 mt-1 flex-wrap">
                          {blackout.service_names.map((name, idx) => (
                            <Badge key={idx} className="bg-purple-100 text-purple-700">
                              {name}
                            </Badge>
                          ))}
                        </div>
                      </div>
                      
                      <div>
                        <span className="text-sm font-medium text-gray-700">Dates: </span>
                        <span className="text-sm text-gray-600">
                          {formatDate(blackout.dates[0])} 
                          {blackout.dates.length > 1 && (
                            <> to {formatDate(blackout.dates[blackout.dates.length - 1])}</>
                          )}
                        </span>
                      </div>
                      
                      {blackout.reason && (
                        <div>
                          <span className="text-sm font-medium text-gray-700">Reason: </span>
                          <span className="text-sm text-gray-600">{blackout.reason}</span>
                        </div>
                      )}
                    </div>
                  </div>

                  {user.role === 'Admin' && (
                    <Button
                      onClick={() => handleDelete(blackout)}
                      variant="outline"
                      size="sm"
                      className="border-red-300 text-red-600 hover:bg-red-50"
                    >
                      <Trash2 className="w-4 h-4 mr-1" />
                      Delete
                    </Button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Create Modal */}
        <Dialog open={showCreateModal} onOpenChange={setShowCreateModal}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle>Create Blackout Days</DialogTitle>
            </DialogHeader>

            <div className="space-y-4 py-4">
              <div>
                <Label>Select Services *</Label>
                <div className="mt-2 space-y-2 max-h-48 overflow-y-auto border border-gray-200 rounded-lg p-3">
                  {services.map(service => (
                    <label
                      key={service.id}
                      className="flex items-center gap-2 cursor-pointer hover:bg-gray-50 p-2 rounded"
                    >
                      <input
                        type="checkbox"
                        checked={formData.service_ids.includes(service.id)}
                        onChange={() => toggleService(service.id)}
                        className="w-4 h-4 text-purple-600 rounded focus:ring-purple-500"
                      />
                      <span className="text-sm text-gray-900">{service.name}</span>
                    </label>
                  ))}
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Start Date *</Label>
                  <Input
                    type="date"
                    value={formData.start_date}
                    onChange={(e) => setFormData({ ...formData, start_date: e.target.value })}
                    className="mt-2"
                  />
                </div>
                <div>
                  <Label>End Date *</Label>
                  <Input
                    type="date"
                    value={formData.end_date}
                    onChange={(e) => setFormData({ ...formData, end_date: e.target.value })}
                    className="mt-2"
                  />
                </div>
              </div>

              <div>
                <Label>Reason (optional)</Label>
                <textarea
                  value={formData.reason}
                  onChange={(e) => setFormData({ ...formData, reason: e.target.value })}
                  placeholder="e.g., Holiday, Staff unavailable, Maintenance..."
                  rows={3}
                  className="w-full mt-2 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                />
              </div>
            </div>

            <div className="flex gap-3">
              <Button
                onClick={() => setShowCreateModal(false)}
                variant="outline"
                className="flex-1"
              >
                Cancel
              </Button>
              <Button
                onClick={handleCreate}
                className="flex-1 bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700"
              >
                Create Blackout
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
};

export default BlackoutDaysPage;
